import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Logo } from "../components/Brand";
import { publicApi, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GoogleButton } from "../lib/googleAuth";

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const [form, setForm] = useState({ name: "", email: "", password: "", is_adult: false, consent: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginParent } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = isSignup ? form : { email: form.email, password: form.password };
      const { data } = await publicApi.post(isSignup ? "/auth/register" : "/auth/login", body);
      loginParent(data);
      nav(isSignup ? "/parent" : "/profiles");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FFF7EC] pt-[env(safe-area-inset-top)]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#3DCCC7] border-r-[3px] border-[#1B1A2E] zn-dots">
        <Link to="/"><Logo /></Link>
        <div className="zn-card p-8 bg-white max-w-md">
          <ShieldCheck size={40} className="mb-4" />
          <h2 className="text-3xl font-bold mb-2">Grown-ups first.</h2>
          <p className="text-[#1B1A2E]/75">Only parents create accounts. Kids get their own colorful profile with a secret PIN — and nothing goes public without you.</p>
        </div>
        <p className="font-display text-lg">Made for curious little creators.</p>
      </div>
      <div className="flex items-start sm:items-center justify-center p-4 sm:p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <form onSubmit={submit} data-testid={`${mode}-form`} className="zn-card zn-pop w-full max-w-md p-6 sm:p-10 space-y-4 sm:space-y-5">
          <div className="lg:hidden"><Logo /></div>
          <h1 className="text-3xl sm:text-4xl font-bold">{isSignup ? "Create family account" : "Welcome back"}</h1>
          <p className="text-[#1B1A2E]/70 -mt-2">{isSignup ? "Takes 30 seconds. You'll add kid profiles next." : "Log in to your parent account."}</p>
          {isSignup && <input data-testid="auth-name-input" className="zn-input" placeholder="Your name" autoComplete="name" value={form.name} onChange={set("name")} required />}
          <input data-testid="auth-email-input" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" className="zn-input" placeholder="Email" value={form.email} onChange={set("email")} required />
          <input data-testid="auth-password-input" type="password" autoComplete={isSignup ? "new-password" : "current-password"} className="zn-input" placeholder={isSignup ? "Password (8+ characters)" : "Password"} value={form.password} onChange={set("password")} required minLength={isSignup ? 8 : 1} />
          {isSignup && (
            <div className="space-y-3 text-sm">
              <label className="flex gap-3 items-start cursor-pointer">
                <input data-testid="auth-adult-checkbox" type="checkbox" className="mt-1 w-5 h-5 accent-[#7B5CFA]" checked={form.is_adult} onChange={(e) => setForm({ ...form, is_adult: e.target.checked })} />
                <span>I'm a parent or legal guardian and I'm 18 or older.</span>
              </label>
              <label className="flex gap-3 items-start cursor-pointer">
                <input data-testid="auth-consent-checkbox" type="checkbox" className="mt-1 w-5 h-5 accent-[#7B5CFA]" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
                <span>I agree to the <Link to="/terms" className="underline font-semibold">Terms</Link> and <Link to="/privacy" className="underline font-semibold">Privacy Policy</Link>, and I consent to my child using Zunii under my supervision.</span>
              </label>
            </div>
          )}
          {err && <p data-testid="auth-error" className="text-[#E8442A] font-semibold text-sm">{err}</p>}
          {!isSignup && <p className="text-right -mt-1"><Link to="/forgot-password" data-testid="auth-forgot-link" className="text-sm font-semibold underline decoration-[#7B5CFA] decoration-2 underline-offset-4">Forgot password?</Link></p>}
          <button data-testid="auth-submit-btn" disabled={busy || (isSignup && !(form.is_adult && form.consent))} className="zn-btn bg-[#FF6B4A] text-white w-full text-lg">
            {busy ? "One sec…" : isSignup ? "Create account" : "Log in"}
          </button>
          <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-[#1B1A2E]/50"><span className="flex-1 h-[2px] bg-[#1B1A2E]/15" />or<span className="flex-1 h-[2px] bg-[#1B1A2E]/15" /></div>
          <GoogleButton path="/profiles" testid={`${mode}-google-btn`} />
          <p className="text-sm text-center">
            {isSignup ? "Already have an account? " : "New to Zunii? "}
            <Link data-testid="auth-switch-link" to={isSignup ? "/login" : "/signup"} className="font-bold underline decoration-[#7B5CFA] decoration-2 underline-offset-4">
              {isSignup ? "Log in" : "Sign up"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
